/**
 * Converts an SRT subtitle string to a valid WebVTT string.
 *
 * Handles common real-world SRT variations:
 *  - Windows (\r\n) and Unix (\n) line endings
 *  - Comma vs dot as the millisecond separator in timestamps
 *  - Missing leading zeros on the hours component
 *  - Extra whitespace around the "-->" arrow
 *  - Multi-line cue text
 *  - Trailing blank lines inside cue blocks
 *  - Non-sequential or missing cue sequence numbers (they are stripped)
 *  - Corrupted / empty cue blocks (silently skipped)
 */
export function convertSrtToVtt(srt: string): string {
  // Normalize line endings to \n
  const normalized = srt.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Split into blocks on one-or-more blank lines
  const rawBlocks = normalized.split(/\n{2,}/);

  const cueParts: string[] = [];

  for (const block of rawBlocks) {
    const lines = block
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length < 2) {
      // Need at least a timing line and one text line
      continue;
    }

    // The first line may be a sequence number — detect by whether the
    // second line looks like a timing line, or the first line itself does.
    let timingLineIndex = 0;
    if (isTimingLine(lines[0]) === false) {
      // Skip the sequence number
      timingLineIndex = 1;
    }

    if (timingLineIndex >= lines.length) continue;

    const timingLine = lines[timingLineIndex];
    if (!isTimingLine(timingLine)) continue;

    const convertedTiming = convertTimingLine(timingLine);
    if (convertedTiming === null) continue;

    const textLines = lines.slice(timingLineIndex + 1);
    if (textLines.length === 0) continue;

    cueParts.push(`${convertedTiming}\n${textLines.join('\n')}`);
  }

  return `WEBVTT\n\n${cueParts.join('\n\n')}`;
}

/**
 * Returns true if a converted VTT string contains at least one cue.
 * Used by the upload pipeline to reject empty conversions before fetching
 * a GCS upload URI.
 */
export function vttHasCues(vtt: string): boolean {
  return vtt.replace(/^WEBVTT\s*/, '').trim().length > 0;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const TIMING_PATTERN =
  /^\d+:[0-5]\d:[0-5]\d[,.]\d{3}\s*-->\s*\d+:[0-5]\d:[0-5]\d[,.]\d{3}/;

function isTimingLine(line: string): boolean {
  return TIMING_PATTERN.test(line);
}

interface ConvertedTimestamp {
  value: string;
  milliseconds: number;
}

function convertTimingLine(line: string): string | null {
  // Normalise surrounding whitespace around the arrow
  const parts = line.split('-->');
  if (parts.length !== 2) return null;

  const start = convertTimestamp(parts[0].trim());
  const end = convertTimestamp(parts[1].trim());

  if (start === null || end === null || end.milliseconds <= start.milliseconds)
    return null;

  return `${start.value} --> ${end.value}`;
}

/**
 * Converts a single SRT timestamp to WebVTT format.
 *
 * SRT:  H:MM:SS,mmm  or  HH:MM:SS,mmm
 * VTT:  HH:MM:SS.mmm
 */
function convertTimestamp(ts: string): ConvertedTimestamp | null {
  // Accept comma or dot as millisecond separator
  const match = ts.match(/^(\d+):([0-5]\d):([0-5]\d)[,.](\d{3})$/);
  if (!match) return null;

  const [, h, m, s, ms] = match;
  const hours = Number(h);
  const minutes = Number(m);
  const seconds = Number(s);
  const milliseconds =
    ((hours * 60 + minutes) * 60 + seconds) * 1000 + Number(ms);

  return {
    value: `${h.padStart(2, '0')}:${m}:${s}.${ms}`,
    milliseconds,
  };
}
